import type { Reading } from '../types';
import type { MetricKey } from '../lib/mapMetric';
import type { KonectReading } from '../lib/konectParse';

/**
 * Wind measured on the Golden Gate Bridge.
 *
 * Every other station on this map is on a shore, a pier or a hull. This one is
 * on the bridge, in the gap the wind actually funnels through, and it reports
 * every five minutes with a gust alongside the mean — a better cadence than
 * anything else here.
 *
 * The bridge district publishes it through a public Konect kiosk. That is not a
 * documented API, so this is written to fail quietly: the station goes
 * unavailable and the rest of the map is untouched.
 */

const API = '/api/ggb';

/** One fetch serves both the marker and the panel, as the whole day comes back. */
let cached: { at: number; rows: Reading[] } | null = null;
let inFlight: Promise<Reading[]> | null = null;

/** Matches the proxy's own cache, so a refresh is a conditional request at worst. */
const TTL_MS = 4 * 60_000;

function toReadings(rows: KonectReading[]): Reading[] {
  return rows.map((r) => ({
    t: new Date(r.t),
    speedKt: r.speedKt,
    dirDeg: r.dirDeg,
    gustKt: r.gustKt,
  }));
}

async function load(): Promise<Reading[]> {
  const fresh = cached && Date.now() - cached.at < TTL_MS;
  if (fresh) return cached!.rows;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const res = await fetch(API);
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`Golden Gate HTTP ${res.status}${detail ? `: ${detail.slice(0, 120)}` : ''}`);
    }

    const body = (await res.json()) as { readings?: KonectReading[] };
    if (!Array.isArray(body.readings) || body.readings.length === 0) {
      throw new Error('No current observation');
    }

    const rows = toReadings(body.readings);
    cached = { at: Date.now(), rows };
    return rows;
  })()
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export async function latest(_stationId: string): Promise<Reading> {
  const rows = await load();
  const newest = rows[rows.length - 1];
  if (!newest) throw new Error('No current observation');
  return newest;
}

export async function history(_stationId: string, hours = 24): Promise<Reading[]> {
  const rows = await load();
  const cutoff = Date.now() - hours * 3600_000;
  return rows.filter((r) => r.t.getTime() >= cutoff);
}

/**
 * The logger carries air temperature, but on a different table — a second
 * request to their service for a number the Bay's own stations already give.
 * Not worth it, so this station simply offers no metrics.
 */
export function latestMetric(_id: string, _metric: MetricKey): number | null {
  return null;
}
